/**
 * Client-side vCard Parser — browser-compatible, zero dependencies
 *
 * Used for instant preview before server round-trip.
 * Supports vCard 2.1, 3.0, 4.0 (iPhone, Android, Outlook).
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unfoldLines(raw) {
  return raw.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '');
}

function decodeQuotedPrintable(str) {
  let decoded = str.replace(/=\r?\n/g, '');
  decoded = decoded.replace(/=([0-9A-Fa-f]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  return decoded;
}

function fixUtf8(str) {
  try {
    // eslint-disable-next-line no-control-regex
    if (/[\x80-\xff]/.test(str)) {
      const bytes = new Uint8Array(str.split('').map(c => c.charCodeAt(0)));
      return new TextDecoder('utf-8').decode(bytes);
    }
  } catch { /* keep original */ }
  return str;
}

function findValueColon(line) {
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') inQuote = !inQuote;
    if (line[i] === ':' && !inQuote) return i;
  }
  return -1;
}

function parseProperty(line) {
  const colonIdx = findValueColon(line);
  if (colonIdx === -1) return null;

  const prefix = line.substring(0, colonIdx);
  const value = line.substring(colonIdx + 1);
  const parts = prefix.split(';');
  const name = parts[0].toUpperCase();
  const params = {};

  for (let i = 1; i < parts.length; i++) {
    const p = parts[i];
    const eqIdx = p.indexOf('=');
    if (eqIdx !== -1) {
      const key = p.substring(0, eqIdx).toUpperCase();
      params[key] = p.substring(eqIdx + 1).toUpperCase();
    } else {
      params.TYPE = (params.TYPE ? params.TYPE + ',' : '') + p.toUpperCase();
    }
  }

  return { name, params, value };
}

/**
 * Normalize phone number: keep digits + leading +
 */
export function normalizePhone(raw) {
  if (!raw) return '';
  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  return hasPlus ? '+' + digits : digits;
}

function phoneType(params) {
  const type = (params.TYPE || '').toUpperCase();
  if (type.includes('CELL') || type.includes('MOBILE')) return 'cell';
  if (type.includes('WORK') || type.includes('VOICE')) return 'work';
  if (type.includes('HOME')) return 'home';
  if (type.includes('FAX')) return 'fax';
  return 'other';
}

// ---------------------------------------------------------------------------
// Category detection (inline — same rules as backend)
// ---------------------------------------------------------------------------

const CATEGORY_RULES = [
  { pattern: /garage|m[ée]cani|r[ée]par|entretien|vidange|r[ée]vision/i, category: 'garage' },
  { pattern: /carross|d[ée]boss|peinture\s*auto/i, category: 'bodywork' },
  { pattern: /pneu|pneum|euromaster|speedy|feu\s*vert|first\s*stop|point\s*s/i, category: 'tire' },
  { pattern: /assur|axa|maif|macif|groupama|allianz|generali|matmut|mma\b|maaf|gan\b|aviva|swisslife|zurich/i, category: 'insurer' },
  { pattern: /court[ié]|brok/i, category: 'broker' },
  { pattern: /carburant|total\s*energ|shell|esso|bp\b|fuel|station.?service/i, category: 'fuel' },
  { pattern: /leas|lld|lcd|ald\b|arval|alphabet|leaseplan|fleet/i, category: 'leasing' },
  { pattern: /vitr|pare.?brise|carglass|saint.?gobain|glass/i, category: 'glass' },
  { pattern: /d[ée]panneu|remorqu|tow|assistance\s*routi/i, category: 'towing' },
  { pattern: /parking|stationn|effia|indigo|vinci\s*park/i, category: 'parking' },
  { pattern: /p[ée]age|t[ée]l[ée]p[ée]age|bip.?go|vinci\s*auto|sanef|aprr/i, category: 'toll' },
  { pattern: /lavage|wash|nettoy|clean/i, category: 'washing' },
  { pattern: /contr[ôo]le\s*tech|dekra|s[ée]curitest|autovision|autosur/i, category: 'inspection' },
  { pattern: /[ée]quipement|accessoir|norauto/i, category: 'equipment' },
  { pattern: /pi[èe]ce|oscaro|autodoc|mister.?auto/i, category: 'parts' },
  { pattern: /location|rental|hertz|europcar|sixt|enterprise|ada\b/i, category: 'rental' },
  { pattern: /expert\s*auto|expertise/i, category: 'expert' },
  { pattern: /juridique|avocat|legal|protection\s*juri/i, category: 'legal' },
  { pattern: /concessionnaire|dealer|renault|peugeot|citro[eë]n|volkswagen|toyota|ford|bmw|mercedes|audi|hyundai|kia|nissan|opel|fiat|dacia|skoda|seat|cupra/i, category: 'dealer' },
];

/**
 * Detect supplier category from name + org text.
 */
export function detectCategory(name, org) {
  const text = `${name || ''} ${org || ''}`.toLowerCase();
  if (!text.trim()) return 'other';
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(text)) return rule.category;
  }
  return 'other';
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

/**
 * Parse a .vcf file text into an array of structured contacts.
 * @param {string} vcfText - Raw .vcf file content
 * @returns {Array<Object>} contacts
 */
export function parseVCardFile(vcfText) {
  if (!vcfText || typeof vcfText !== 'string') return [];

  const normalized = unfoldLines(vcfText.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
  const lines = normalized.split('\n');
  const blocks = [];
  let current = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.toUpperCase() === 'BEGIN:VCARD') {
      current = [];
    } else if (trimmed.toUpperCase() === 'END:VCARD') {
      if (current) { blocks.push(current); current = null; }
    } else if (current !== null && trimmed) {
      current.push(trimmed);
    }
  }

  return blocks.map(parseOneVCard).filter(c => c.name);
}

function parseOneVCard(lines) {
  const contact = {
    name: '', firstName: '', lastName: '', org: '', title: '',
    phones: [], emails: [],
    address: { street: '', city: '', region: '', postal: '', country: '' },
    url: '', note: '', categories: '',
  };

  for (const line of lines) {
    const prop = parseProperty(line);
    if (!prop) continue;

    let val = prop.value;
    if (prop.params.ENCODING === 'QUOTED-PRINTABLE') {
      val = fixUtf8(decodeQuotedPrintable(val));
    }
    if (prop.name === 'PHOTO' || prop.name === 'LOGO') continue;

    switch (prop.name) {
      case 'FN':
        contact.name = val.trim();
        break;
      case 'N': {
        const nParts = val.split(';');
        contact.lastName = (nParts[0] || '').trim();
        contact.firstName = (nParts[1] || '').trim();
        if (!contact.name) {
          contact.name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
        }
        break;
      }
      case 'ORG':
        contact.org = val.split(';')[0].trim();
        break;
      case 'TITLE':
        contact.title = val.trim();
        break;
      case 'TEL': {
        const num = normalizePhone(val);
        if (num) contact.phones.push({ number: num, type: phoneType(prop.params), raw: val.trim() });
        break;
      }
      case 'EMAIL':
        if (val.trim()) contact.emails.push({ address: val.trim(), type: (prop.params.TYPE || 'other').toLowerCase() });
        break;
      case 'ADR': {
        const parts = val.split(';');
        contact.address.street = (parts[2] || '').trim();
        contact.address.city = (parts[3] || '').trim();
        contact.address.region = (parts[4] || '').trim();
        contact.address.postal = (parts[5] || '').trim();
        contact.address.country = (parts[6] || '').trim();
        break;
      }
      case 'URL': contact.url = val.trim(); break;
      case 'NOTE': contact.note = val.replace(/\\n/g, '\n').replace(/\\,/g, ',').trim(); break;
      case 'CATEGORIES': contact.categories = val.trim(); break;
      default: break;
    }
  }

  if (!contact.name && contact.org) contact.name = contact.org;
  return contact;
}

/**
 * Map a parsed contact to supplier-like preview object.
 */
export function mapContactToPreview(contact) {
  const category = detectCategory(contact.name, contact.org);
  const cellPhone = contact.phones.find(p => p.type === 'cell');
  const workPhone = contact.phones.find(p => p.type === 'work' || p.type === 'other');
  const anyPhone = contact.phones[0];

  return {
    name: contact.name,
    org: contact.org,
    category,
    mobile: cellPhone?.number || null,
    phone: workPhone?.number || cellPhone?.number || anyPhone?.number || null,
    email: contact.emails[0]?.address || null,
    city: contact.address.city || null,
    country: contact.address.country || 'FR',
  };
}
